import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { ErrorText } from "../components/ui/feedback";
import { Field } from "../components/ui/Field";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { adminApi } from "./api";
import { QueryError } from "./dense";
import { adminWorkspaceMembersQuery } from "./queries";
import type { AdminWorkspace } from "./types";

export interface TransferOwnershipModalProps {
  workspace: AdminWorkspace;
  onClose: () => void;
  onDone: () => void;
}

/** Picks one of the workspace's members as the new owner. The current owner is not offered. */
export function TransferOwnershipModal({
  workspace,
  onClose,
  onDone,
}: TransferOwnershipModalProps) {
  const { t } = useI18n();
  const members = useQuery(adminWorkspaceMembersQuery(workspace.id));
  const candidates = (members.data ?? []).filter((member) => member.role !== "owner");
  const [picked, setPicked] = useState<string | null>(null);
  const operatorId = picked ?? candidates[0]?.operatorId ?? "";

  const transfer = useMutation({
    mutationFn: () => adminApi.transferOwnership(workspace.id, operatorId),
    onSuccess: onDone,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (operatorId) transfer.mutate();
  };

  return (
    <Modal title={t("admin.transfer.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-note text-ink-2">
          {t("admin.transfer.message", { name: workspace.name })}
        </p>
        {members.isError ? (
          <QueryError error={members.error} onRetry={() => void members.refetch()} />
        ) : null}
        {candidates.length === 0 && !members.isPending && !members.isError ? (
          <p className="text-operate text-ink">{t("admin.transfer.none")}</p>
        ) : null}
        {candidates.length > 0 ? (
          <Field label={t("admin.transfer.pick")}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={operatorId}
                onChange={(event) => setPicked(event.target.value)}
              >
                {candidates.map((member) => (
                  <option key={member.operatorId} value={member.operatorId}>
                    {member.name || member.email}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        <ErrorText error={transfer.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={transfer.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={transfer.isPending}
            disabled={!operatorId}
          >
            {t("admin.action.transfer")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
